import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  api,
  ApiError,
  type AmountSource,
  type OperatorDictionaries,
  type Preview,
  type SampleDocument,
  type Scheme,
  type SchemeDictionaries,
  type SchemeDraft,
  type SchemeKind,
  type SchemeLineDraft,
  type TemplateSummary,
} from '../api';
import { date, money, type Dictionary, type Locale } from '../i18n';
import * as Icon from '@firmeto/design/Icons';

/**
 * Schematy księgowania wzorcowe (E7-S2, E7-S8).
 *
 * Schemat systemowy działa u wszystkich firm, więc konto wskazuje rolą — numer jest własnością
 * planu jednej firmy. Zmiana to nowa wersja od wskazanej daty: stara kończy się dzień wcześniej,
 * a dziennik nie przelicza się wstecz. Podgląd dekretu liczy ten sam kod, którym księguje
 * aplikacja, na kontach wybranego wzorca i przykładowych kwotach — edytor pokazuje dokładnie to,
 * co dostanie księgowa.
 */

function failureText(failure: unknown): string {
  return failure instanceof ApiError ? failure.message : String(failure);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

type Status = 'current' | 'future' | 'closed' | 'inactive';

function statusOf(scheme: Scheme, on = today()): Status {
  if (!scheme.active) return 'inactive';
  if (scheme.validTo && scheme.validTo < on) return 'closed';
  if (scheme.validFrom > on) return 'future';
  return 'current';
}

function StatusChip({ scheme, t, locale }: { scheme: Scheme; t: Dictionary; locale: Locale }) {
  const status = statusOf(scheme);
  if (status === 'inactive') return <span className="chip st-warn">{t.schemes.inactive}</span>;
  if (status === 'closed') return <span className="chip st-draft">{t.schemes.closed}</span>;
  if (status === 'future')
    return (
      <span className="chip st-info">
        {t.schemes.future} {date(scheme.validFrom, locale)}
      </span>
    );
  return <span className="chip st-ok">{t.schemes.current}</span>;
}

function condition(scheme: { counterpartyType: string | null; costCategory: string | null }, t: Dictionary): string {
  const parts = [
    scheme.counterpartyType ? `${t.schemes.counterpartyType}: ${t.dict.counterpartyTypes[scheme.counterpartyType.toUpperCase()] ?? scheme.counterpartyType}` : null,
    scheme.costCategory ? `${t.schemes.costCategory}: ${t.dict.costCategories[scheme.costCategory.toUpperCase()] ?? scheme.costCategory}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

export function Schemes({ t, locale, onOpen, onNew }: { t: Dictionary; locale: Locale; onOpen: (schemeId: string) => void; onNew: () => void }) {
  const [items, setItems] = useState<Scheme[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.schemes().then(setItems).catch((failure) => setError(failureText(failure)));
  }, []);

  const kinds: SchemeKind[] = ['INVOICE', 'CORRECTION', 'COST'];

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.schemes.title}</h3>
          <p>{t.schemes.lead}</p>
        </div>
        <div className="sp">
          <button type="button" className="btn" onClick={onNew}>
            <Icon.Plus />
            {t.schemes.new}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {items === null && <p className="muted">{t.loading}</p>}
      {items && items.length === 0 && (
        <div className="panel">
          <p>{t.schemes.none}</p>
        </div>
      )}

      {items &&
        kinds
          .filter((kind) => items.some((scheme) => scheme.documentKind === kind))
          .map((kind) => (
            <div className="panel" key={kind}>
              <div className="hd">
                <div>
                  <h4>{t.dict.schemeKinds[kind]}</h4>
                </div>
              </div>
              <div className="tbl">
                <table>
                  <thead>
                    <tr>
                      <th className="wrap">{t.schemes.name}</th>
                      <th className="wrap">{t.schemes.condition}</th>
                      <th>{t.schemes.validity}</th>
                      <th className="n">{t.schemes.lines}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items
                      .filter((scheme) => scheme.documentKind === kind)
                      .map((scheme) => (
                        <tr key={scheme.id}>
                          <td className="wrap">
                            <button type="button" className="btn ghost sm" onClick={() => onOpen(scheme.id)}>
                              <b>{scheme.name}</b>
                            </button>{' '}
                            <StatusChip scheme={scheme} t={t} locale={locale} />
                          </td>
                          <td className="wrap">{condition(scheme, t)}</td>
                          <td className="mono">
                            {date(scheme.validFrom, locale)} – {scheme.validTo ? date(scheme.validTo, locale) : t.schemes.openEnded}
                          </td>
                          <td className="n mono">{scheme.lines.length}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
    </>
  );
}

// MARK: - Edytor

const emptyDraft = (): SchemeDraft => ({
  name: '',
  documentKind: 'INVOICE',
  counterpartyType: null,
  costCategory: null,
  validFrom: today(),
  validTo: null,
  lines: [
    { accountRole: 'RECEIVABLES', side: 'DEBIT', amountSource: 'GROSS', withCounterparty: true, description: null },
    { accountRole: 'SALES_SERVICES', side: 'CREDIT', amountSource: 'NET', withCounterparty: false, description: null },
  ],
});

/** Schemat z API jako szkic nowej wersji — te same wiersze, data początku do wskazania. */
function draftOf(scheme: Scheme): SchemeDraft {
  return {
    name: scheme.name,
    documentKind: scheme.documentKind,
    counterpartyType: (scheme.counterpartyType?.toUpperCase() as SchemeDraft['counterpartyType']) ?? null,
    costCategory: (scheme.costCategory?.toUpperCase() as SchemeDraft['costCategory']) ?? null,
    validFrom: today(),
    validTo: null,
    lines: scheme.lines.map((line) => ({
      accountRole: (line.accountRole ?? '').toUpperCase(),
      side: line.side,
      amountSource: line.amountSource,
      withCounterparty: line.withCounterparty,
      description: line.description,
    })),
  };
}

export function SchemeEditor({
  schemeId,
  t,
  locale,
  onBack,
  onSaved,
}: {
  /** Brak — nowy schemat; z identyfikatorem — podgląd z możliwością nowej wersji. */
  schemeId: string | null;
  t: Dictionary;
  locale: Locale;
  onBack: () => void;
  onSaved: (schemeId: string) => void;
}) {
  const [scheme, setScheme] = useState<Scheme | null>(null);
  const [draft, setDraft] = useState<SchemeDraft | null>(schemeId ? null : emptyDraft());
  const [dictionaries, setDictionaries] = useState<OperatorDictionaries | null>(null);
  const [schemeDictionaries, setSchemeDictionaries] = useState<SchemeDictionaries | null>(null);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [meta, setMeta] = useState({ name: '', active: true });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!schemeId) return;
    try {
      const loaded = await api.scheme(schemeId);
      setScheme(loaded);
      setMeta({ name: loaded.name, active: loaded.active });
    } catch (failure) {
      setError(failureText(failure));
    }
  }, [schemeId]);

  useEffect(() => {
    void load();
    Promise.all([api.dictionaries(), api.schemeDictionaries(), api.templates()])
      .then(([roles, kinds, loadedTemplates]) => {
        setDictionaries(roles);
        setSchemeDictionaries(kinds);
        setTemplates(loadedTemplates.filter((template) => template.active));
      })
      .catch((failure) => setError(failureText(failure)));
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await action();
      setSaved(true);
    } catch (failure) {
      setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!draft) return;
    const payload: SchemeDraft = {
      ...draft,
      name: draft.name.trim(),
      costCategory: draft.documentKind === 'COST' ? draft.costCategory : null,
      lines: draft.lines.map((line) => ({ ...line, description: line.description?.trim() || null })),
    };
    await run(async () => {
      const result = schemeId ? await api.newSchemeVersion(schemeId, payload) : await api.createScheme(payload);
      onSaved(result.id);
    });
  }

  async function saveMeta(event: React.FormEvent) {
    event.preventDefault();
    if (!schemeId) return;
    await run(async () => setScheme(await api.updateScheme(schemeId, meta.name.trim(), meta.active)));
  }

  if (schemeId && !scheme) {
    return (
      <>
        {error && (
          <div className="alert a-bad">
            <Icon.Error />
            <div>
              <b>{error}</b>
            </div>
          </div>
        )}
        <p className="muted">{t.loading}</p>
      </>
    );
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>
            {scheme ? scheme.name : t.schemes.newTitle} {scheme && <StatusChip scheme={scheme} t={t} locale={locale} />}
          </h3>
          {scheme && (
            <p>
              {t.dict.schemeKinds[scheme.documentKind]} · {condition(scheme, t)} · {t.schemes.validity.toLowerCase()} {date(scheme.validFrom, locale)} –{' '}
              {scheme.validTo ? date(scheme.validTo, locale) : t.schemes.openEnded}
            </p>
          )}
        </div>
        <div className="sp">
          <button type="button" className="btn ghost" onClick={onBack}>
            {t.schemes.back}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {saved && !error && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{t.schemes.saved}</b>
          </div>
        </div>
      )}

      {scheme && !draft && (
        <>
          <div className="grid two">
            <div className="panel">
              <div className="hd">
                <div>
                  <h4>{t.schemes.lines}</h4>
                </div>
                <div className="sp">
                  <button type="button" className="btn sm" onClick={() => setDraft(draftOf(scheme))} disabled={statusOf(scheme) === 'closed'}>
                    {t.schemes.newVersion}
                  </button>
                </div>
              </div>
              <div className="tbl">
                <table>
                  <thead>
                    <tr>
                      <th className="wrap">{t.schemes.role}</th>
                      <th>{t.schemes.side}</th>
                      <th className="wrap">{t.schemes.source}</th>
                      <th className="wrap">{t.schemes.description}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheme.lines.map((line) => (
                      <tr key={line.lineNo}>
                        <td className="wrap">
                          {line.accountRole ? t.dict.roles[line.accountRole.toUpperCase()] ?? line.accountRole : <span className="mono">{line.accountNumber}</span>}
                          {line.withCounterparty && <span className="chip st-info">{t.schemes.withCounterparty}</span>}
                        </td>
                        <td>{t.dict.entrySides[line.side]}</td>
                        <td className="wrap">{t.dict.sources[line.amountSource]}</td>
                        <td className="wrap">{line.description ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <form className="panel" onSubmit={(event) => void saveMeta(event)}>
              <div className="hd">
                <div>
                  <h4>{t.schemes.rename}</h4>
                </div>
              </div>
              <label className="fld">
                <span>{t.schemes.name}</span>
                <input className="inp" value={meta.name} onChange={(event) => setMeta({ ...meta, name: event.target.value })} required />
              </label>
              <label className="row" style={{ alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={meta.active} onChange={(event) => setMeta({ ...meta, active: event.target.checked })} />
                <span>{t.schemes.activeLabel}</span>
              </label>
              <div className="row">
                <button type="submit" className="btn" disabled={busy || meta.name.trim() === ''}>
                  {t.templates.save}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {draft && dictionaries && schemeDictionaries && (
        <SchemeForm
          t={t}
          locale={locale}
          draft={draft}
          isVersion={Boolean(scheme)}
          previous={scheme}
          roles={dictionaries.accountRoles.map((role) => role.toUpperCase())}
          settlementRoles={dictionaries.settlementRoles.map((role) => role.toUpperCase())}
          dictionaries={schemeDictionaries}
          templates={templates}
          busy={busy}
          onChange={setDraft}
          onSave={() => void save()}
          onCancel={scheme ? () => setDraft(null) : onBack}
        />
      )}
      {draft && (!dictionaries || !schemeDictionaries) && !error && <p className="muted">{t.loading}</p>}
    </>
  );
}

function SchemeForm({
  t,
  locale,
  draft,
  isVersion,
  previous,
  roles,
  settlementRoles,
  dictionaries,
  templates,
  busy,
  onChange,
  onSave,
  onCancel,
}: {
  t: Dictionary;
  locale: Locale;
  draft: SchemeDraft;
  isVersion: boolean;
  previous: Scheme | null;
  roles: string[];
  settlementRoles: string[];
  dictionaries: SchemeDictionaries;
  templates: TemplateSummary[];
  busy: boolean;
  onChange: (draft: SchemeDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const isCost = draft.documentKind === 'COST';
  const sources = dictionaries.amountSources.filter((source) => isCost || !dictionaries.costOnlySources.includes(source));

  function changeKind(kind: SchemeKind) {
    const costOnly = new Set(dictionaries.costOnlySources);
    onChange({
      ...draft,
      documentKind: kind,
      costCategory: kind === 'COST' ? draft.costCategory : null,
      // Źródło kosztowe na fakturze nie ma sensu — wracamy do netto, zamiast czekać na błąd z API
      lines: draft.lines.map((line) => (kind !== 'COST' && costOnly.has(line.amountSource) ? { ...line, amountSource: 'NET' as AmountSource } : line)),
    });
  }

  function changeLine(index: number, patch: Partial<SchemeLineDraft>) {
    const lines = draft.lines.map((line, i) => (i === index ? { ...line, ...patch } : line));
    const changed = lines[index];
    // Konto rozrachunkowe zawsze z kontrahentem — reguła dziennika, nie wybór operatora
    if (changed && settlementRoles.includes(changed.accountRole)) changed.withCounterparty = true;
    onChange({ ...draft, lines });
  }

  const complete = draft.name.trim() !== '' && draft.lines.length >= 2 && draft.lines.every((line) => line.accountRole !== '');

  return (
    <>
      <div className="panel">
        <div className="hd">
          <div>
            <h4>{isVersion ? t.schemes.newVersion : t.schemes.newTitle}</h4>
            {isVersion && <p>{t.schemes.versionLead}</p>}
          </div>
        </div>
        <div className="row">
          <label className="fld" style={{ flex: 2, minWidth: 260 }}>
            <span>{t.schemes.name}</span>
            <input className="inp" value={draft.name} onChange={(event) => onChange({ ...draft, name: event.target.value })} required autoFocus={!isVersion} />
          </label>
          <label className="fld" style={{ flex: 1, minWidth: 200 }}>
            <span>{t.schemes.kind}</span>
            <select className="inp" value={draft.documentKind} disabled={isVersion} onChange={(event) => changeKind(event.target.value as SchemeKind)}>
              {dictionaries.documentKinds.map((kind) => (
                <option key={kind} value={kind}>
                  {t.dict.schemeKinds[kind]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="row">
          <label className="fld" style={{ flex: 1, minWidth: 200 }}>
            <span>{t.schemes.counterpartyType}</span>
            <select
              className="inp"
              value={draft.counterpartyType ?? ''}
              onChange={(event) => onChange({ ...draft, counterpartyType: (event.target.value || null) as SchemeDraft['counterpartyType'] })}
            >
              <option value="">{t.schemes.anyCounterparty}</option>
              {dictionaries.counterpartyTypes.map((type) => (
                <option key={type} value={type}>
                  {t.dict.counterpartyTypes[type]}
                </option>
              ))}
            </select>
          </label>
          {isCost && (
            <label className="fld" style={{ flex: 1, minWidth: 200 }}>
              <span>{t.schemes.costCategory}</span>
              <select
                className="inp"
                value={draft.costCategory ?? ''}
                onChange={(event) => onChange({ ...draft, costCategory: (event.target.value || null) as SchemeDraft['costCategory'] })}
              >
                <option value="">{t.schemes.anyCategory}</option>
                {dictionaries.costCategories.map((category) => (
                  <option key={category} value={category}>
                    {t.dict.costCategories[category]}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="fld">
            <span>{t.schemes.validFrom}</span>
            <input
              className="inp mono"
              type="date"
              value={draft.validFrom}
              min={previous ? previous.validFrom : undefined}
              onChange={(event) => onChange({ ...draft, validFrom: event.target.value })}
              required
            />
          </label>
          <label className="fld">
            <span>{t.schemes.validTo}</span>
            <input className="inp mono" type="date" value={draft.validTo ?? ''} onChange={(event) => onChange({ ...draft, validTo: event.target.value || null })} />
          </label>
        </div>

        <div className="hd">
          <div>
            <h4>{t.schemes.lines}</h4>
            <p>{t.schemes.settlementHint}</p>
          </div>
          <div className="sp">
            <button
              type="button"
              className="btn ghost sm"
              onClick={() =>
                onChange({
                  ...draft,
                  lines: [...draft.lines, { accountRole: '', side: 'CREDIT', amountSource: 'NET', withCounterparty: false, description: null }],
                })
              }
            >
              <Icon.Plus />
              {t.schemes.addLine}
            </button>
          </div>
        </div>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th className="wrap">{t.schemes.role}</th>
                <th>{t.schemes.side}</th>
                <th className="wrap">{t.schemes.source}</th>
                <th>{t.schemes.withCounterparty}</th>
                <th className="wrap">{t.schemes.description}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {draft.lines.map((line, index) => {
                const settlement = settlementRoles.includes(line.accountRole);
                return (
                  <tr key={index}>
                    <td className="wrap">
                      <select className="inp" aria-label={`${t.schemes.role} ${index + 1}`} value={line.accountRole} onChange={(event) => changeLine(index, { accountRole: event.target.value })}>
                        <option value="">—</option>
                        {roles.map((role) => (
                          <option key={role} value={role}>
                            {t.dict.roles[role] ?? role}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select className="inp" aria-label={`${t.schemes.side} ${index + 1}`} value={line.side} onChange={(event) => changeLine(index, { side: event.target.value as SchemeLineDraft['side'] })}>
                        <option value="DEBIT">{t.dict.entrySides.DEBIT}</option>
                        <option value="CREDIT">{t.dict.entrySides.CREDIT}</option>
                      </select>
                    </td>
                    <td className="wrap">
                      <select className="inp" aria-label={`${t.schemes.source} ${index + 1}`} value={line.amountSource} onChange={(event) => changeLine(index, { amountSource: event.target.value as AmountSource })}>
                        {sources.map((source) => (
                          <option key={source} value={source}>
                            {t.dict.sources[source]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`${t.schemes.withCounterparty} ${index + 1}`}
                        checked={line.withCounterparty || settlement}
                        disabled={settlement}
                        onChange={(event) => changeLine(index, { withCounterparty: event.target.checked })}
                      />
                    </td>
                    <td className="wrap">
                      <input className="inp" value={line.description ?? ''} onChange={(event) => changeLine(index, { description: event.target.value || null })} />
                    </td>
                    <td className="n">
                      <button
                        type="button"
                        className="btn ghost sm danger"
                        aria-label={`${t.schemes.removeLine} ${index + 1}`}
                        onClick={() => onChange({ ...draft, lines: draft.lines.filter((_, i) => i !== index) })}
                        disabled={draft.lines.length <= 2}
                      >
                        {t.schemes.removeLine}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="row">
          <button type="button" className="btn" onClick={onSave} disabled={busy || !complete}>
            {isVersion ? t.schemes.saveVersion : t.schemes.save}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
            {t.schemes.cancel}
          </button>
        </div>
      </div>

      <PreviewPanel t={t} locale={locale} draft={draft} templates={templates} ready={complete} />
    </>
  );
}

/** Dekret na przykładowych kwotach — liczony po stronie API tym samym kodem, co księgowanie. */
function PreviewPanel({ t, locale, draft, templates, ready }: { t: Dictionary; locale: Locale; draft: SchemeDraft; templates: TemplateSummary[]; ready: boolean }) {
  const [templateId, setTemplateId] = useState('');
  const [sample, setSample] = useState({ net: '1000,00', vat: '230,00', vatDeductible: '230,00', withCounterparty: true });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!templateId && templates.length > 0) setTemplateId(templates[0]!.id);
  }, [templates, templateId]);

  const request = useMemo(() => JSON.stringify({ draft, templateId, sample }), [draft, templateId, sample]);

  useEffect(() => {
    if (!ready || !templateId) {
      setPreview(null);
      return;
    }
    const body: SampleDocument = {
      net: sample.net.replace(',', '.').trim() || '0',
      vat: sample.vat.replace(',', '.').trim() || '0',
      vatDeductible: draft.documentKind === 'COST' ? sample.vatDeductible.replace(',', '.').trim() || '0' : undefined,
      withCounterparty: sample.withCounterparty,
    };
    const timer = setTimeout(() => {
      api
        .previewScheme(draft, templateId, body)
        .then((result) => {
          setPreview(result);
          setError(null);
        })
        .catch((failure) => {
          setPreview(null);
          setError(failureText(failure));
        });
    }, 300);
    return () => clearTimeout(timer);
    // `request` jest kluczem treści — porównanie po wartości, nie po tożsamości obiektów
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, ready]);

  const sum = (side: 'DEBIT' | 'CREDIT') => preview?.lines.filter((line) => line.side === side).reduce((acc, line) => acc + Number(line.amount), 0) ?? 0;

  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>{t.schemes.preview}</h4>
          <p>{t.schemes.previewLead}</p>
        </div>
      </div>
      <div className="row">
        <label className="fld" style={{ flex: 2, minWidth: 260 }}>
          <span>{t.schemes.onTemplate}</span>
          <select className="inp" value={templateId} onChange={(event) => setTemplateId(event.target.value)}>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </label>
        <label className="fld">
          <span>{t.schemes.sampleNet}</span>
          <input className="inp mono" style={{ maxWidth: 140 }} inputMode="decimal" value={sample.net} onChange={(event) => setSample({ ...sample, net: event.target.value })} />
        </label>
        <label className="fld">
          <span>{t.schemes.sampleVat}</span>
          <input className="inp mono" style={{ maxWidth: 140 }} inputMode="decimal" value={sample.vat} onChange={(event) => setSample({ ...sample, vat: event.target.value })} />
        </label>
        {draft.documentKind === 'COST' && (
          <label className="fld">
            <span>{t.schemes.sampleDeductible}</span>
            <input
              className="inp mono"
              style={{ maxWidth: 140 }}
              inputMode="decimal"
              value={sample.vatDeductible}
              onChange={(event) => setSample({ ...sample, vatDeductible: event.target.value })}
            />
          </label>
        )}
        <label className="row" style={{ alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={sample.withCounterparty} onChange={(event) => setSample({ ...sample, withCounterparty: event.target.checked })} />
          <span>{t.schemes.sampleCounterparty}</span>
        </label>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {!preview && !error && <p className="muted">{t.schemes.previewEmpty}</p>}
      {preview && (
        <>
          {preview.postable ? (
            <div className="alert a-ok">
              <Icon.Check />
              <div>{t.schemes.postable}</div>
            </div>
          ) : (
            <div className="alert a-warn">
              <Icon.Warn />
              <div>
                <b>{t.schemes.notPostable}</b> {preview.problem}
              </div>
            </div>
          )}
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.schemes.account}</th>
                  <th className="wrap">{t.templates.accountName}</th>
                  <th className="n">{t.schemes.debit}</th>
                  <th className="n">{t.schemes.credit}</th>
                </tr>
              </thead>
              <tbody>
                {preview.lines.map((line, index) => (
                  <tr key={index}>
                    <td className="mono">{line.account}</td>
                    <td className="wrap">
                      {line.accountName}
                      {line.withCounterparty && <span className="chip st-info">{t.schemes.withCounterparty}</span>}
                    </td>
                    <td className="n mono">{line.side === 'DEBIT' ? money(line.amount, locale) : ''}</td>
                    <td className="n mono">{line.side === 'CREDIT' ? money(line.amount, locale) : ''}</td>
                  </tr>
                ))}
                <tr>
                  <td />
                  <td>
                    <b>{t.schemes.total}</b>
                  </td>
                  <td className="n mono">
                    <b>{money(sum('DEBIT'), locale)}</b>
                  </td>
                  <td className="n mono">
                    <b>{money(sum('CREDIT'), locale)}</b>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
